import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Coins, CheckCircle2, History } from "lucide-react";
import { formatBRL } from "@/lib/api";
import { toast } from "sonner";

export default function PaymentControl() {
    const { children, activeChildId } = useApp();
    const activeChild = children.find((c) => c.id === activeChildId);
    const [summary, setSummary] = useState({});
    const [history, setHistory] = useState([]);
    const [checkins, setCheckins] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [s, h, c] = await Promise.all([
            api.get("/payments/summary"),
            activeChildId ? api.get("/payments/history", { params: { child_id: activeChildId } }) : Promise.resolve({ data: [] }),
            activeChildId ? api.get("/checkins", { params: { child_id: activeChildId } }) : Promise.resolve({ data: [] }),
        ]);
        setSummary(s.data || {});
        setHistory(h.data || []);
        setCheckins(c.data || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeChildId]);

    const payPending = async () => {
        if (!activeChildId) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/payments/pay`, null, { params: { child_id: activeChildId } });
            if (data.paid_amount > 0) toast.success(`Pago ${formatBRL(data.paid_amount)} para ${activeChild?.name}`);
            else toast.info("Nenhum valor pendente");
            load();
        } finally {
            setBusy(false);
        }
    };

    if (!activeChildId) {
        return <Card className="p-10 border-[#E2DCD5] text-center"><p className="text-slate-600">Adicione um filho primeiro.</p></Card>;
    }

    const stats = summary[activeChildId] || { pending: 0, paid: 0, pending_count: 0, paid_count: 0 };
    const pendingCheckins = checkins.filter((c) => !c.is_paid && c.reward_amount_snapshot > 0);

    return (
        <div className="space-y-6">
            <div className="rise">
                <div className="text-xs uppercase tracking-widest text-[#8a7968]">Mesada & pagamentos</div>
                <h1 className="text-3xl sm:text-4xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>
                    Controle de <span className="text-amber-600">{activeChild?.name}</span>
                </h1>
            </div>

            <div className="grid md:grid-cols-3 gap-4 rise rise-1">
                <Card className="p-5 border-amber-200 bg-amber-50/40" data-testid="pay-pending-card">
                    <div className="text-xs uppercase tracking-widest text-amber-800">A pagar</div>
                    <div className="text-3xl font-bold mt-1 text-amber-900" style={{ fontFamily: "Outfit" }}>{formatBRL(stats.pending)}</div>
                    <div className="text-xs text-amber-700 mt-1">{stats.pending_count || 0} tarefa(s)</div>
                    <Button
                        onClick={payPending}
                        disabled={busy || !stats.pending}
                        data-testid="pay-pending-button"
                        className="mt-3 bg-amber-600 hover:bg-amber-700 text-white rounded-full"
                    >
                        <Coins className="h-4 w-4 mr-1.5" /> Marcar como pago
                    </Button>
                </Card>
                <Card className="p-5 border-emerald-200" data-testid="pay-paid-card">
                    <div className="text-xs uppercase tracking-widest text-emerald-800">Já pago</div>
                    <div className="text-3xl font-bold mt-1 text-emerald-900" style={{ fontFamily: "Outfit" }}>{formatBRL(stats.paid)}</div>
                    <div className="text-xs text-emerald-700 mt-1">{stats.paid_count || 0} tarefa(s)</div>
                </Card>
                <Card className="p-5 border-[#E2DCD5]">
                    <div className="text-xs uppercase tracking-widest text-[#8a7968]">Total acumulado</div>
                    <div className="text-3xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{formatBRL((stats.pending || 0) + (stats.paid || 0))}</div>
                    <div className="text-xs text-slate-500 mt-1">Vida toda</div>
                </Card>
            </div>

            <Card className="p-6 border-[#E2DCD5] rise rise-2">
                <div className="flex items-center gap-2 mb-3">
                    <Coins className="h-5 w-5 text-amber-600" />
                    <h2 className="font-semibold" style={{ fontFamily: "Outfit" }}>Tarefas pendentes de pagamento</h2>
                </div>
                {pendingCheckins.length === 0 ? (
                    <p className="text-sm text-slate-500">Sem nada pendente agora. 👏</p>
                ) : (
                    <ul className="divide-y divide-[#E2DCD5]">
                        {pendingCheckins.map((c) => (
                            <li key={c.id} className="flex items-center justify-between py-2.5 text-sm" data-testid={`pending-checkin-${c.id}`}>
                                <div>
                                    <div className="text-slate-800">{new Date(c.date + "T00:00").toLocaleDateString("pt-BR")}</div>
                                    <div className="text-xs text-slate-500">Check-in</div>
                                </div>
                                <span className="chip bg-amber-100 text-amber-900">{formatBRL(c.reward_amount_snapshot)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>

            <Card className="p-6 border-[#E2DCD5] rise rise-3">
                <div className="flex items-center gap-2 mb-3">
                    <History className="h-5 w-5 text-emerald-700" />
                    <h2 className="font-semibold" style={{ fontFamily: "Outfit" }}>Histórico de pagamentos</h2>
                </div>
                {history.length === 0 ? (
                    <p className="text-sm text-slate-500">Nenhum pagamento registrado ainda.</p>
                ) : (
                    <ul className="divide-y divide-[#E2DCD5]">
                        {history.map((p) => (
                            <li key={p.id} className="flex items-center justify-between py-3" data-testid={`payment-${p.id}`}>
                                <div className="flex items-center gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                    <div>
                                        <div className="font-medium text-slate-800">{formatBRL(p.amount)}</div>
                                        <div className="text-xs text-slate-500">
                                            {p.checkin_count} tarefa(s) · {new Date(p.paid_at).toLocaleString("pt-BR")}
                                        </div>
                                    </div>
                                </div>
                                <span className="chip shimmer text-emerald-900 border border-emerald-200">PAGO</span>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        </div>
    );
}
